import { logger } from './logger'
import { getDirectHitTransactionId } from './everflow'
import { applySourceFromFunnelGetData } from './trafficSource'

// Full funnel-get-data blob. A new tab still has the previous session in
// memory, and FunnelContext saves that memory on every change. Until memory
// is replaced by this snapshot, those saves must write the API response.
const pendingLoadSnapshotByStorageKey = new Map()

const samePlainData = (current, expected) => {
  if (!expected) return true
  if (!current) return false
  const currentKeys = Object.keys(current)
  const expectedKeys = Object.keys(expected)
  if (currentKeys.length !== expectedKeys.length) return false
  return expectedKeys.every((key) => Object.is(current[key], expected[key]))
}

const rememberLoadSnapshot = (storageKey, snapshot) => {
  if (!storageKey || !snapshot?.data) return
  pendingLoadSnapshotByStorageKey.set(storageKey, snapshot)
}

/**
 * Replace a stored funnel record with the funnel-get-data snapshot while a
 * load is still settling. `storedRecord` is the full localStorage object.
 */
export const applyLoadOverlay = (storageKey, storedRecord) => {
  const snapshot = storageKey ? pendingLoadSnapshotByStorageKey.get(storageKey) : null
  if (!snapshot?.data) return storedRecord
  return {
    ...(storedRecord || {}),
    data: snapshot.data,
    ...(snapshot.funnelSteps ? { funnelSteps: snapshot.funnelSteps } : {}),
    ...(snapshot.funnelHistory ? { funnelHistory: snapshot.funnelHistory } : {}),
  }
}

export const reconcilePersistedFunnelData = (storageKey, funnelData, funnelSteps, funnelHistory) => {
  const snapshot = storageKey ? pendingLoadSnapshotByStorageKey.get(storageKey) : null
  if (!snapshot?.data) {
    return {
      data: funnelData,
      funnelSteps,
      funnelHistory,
      replaceState: null,
    }
  }

  const memoryMatchesSnapshot =
    samePlainData(funnelData, snapshot.data) &&
    samePlainData(funnelSteps, snapshot.funnelSteps) &&
    samePlainData(funnelHistory, snapshot.funnelHistory)

  if (memoryMatchesSnapshot) {
    pendingLoadSnapshotByStorageKey.delete(storageKey)
    return {
      data: funnelData,
      funnelSteps,
      funnelHistory,
      replaceState: null,
    }
  }

  return {
    data: snapshot.data,
    funnelSteps: snapshot.funnelSteps || funnelSteps,
    funnelHistory: snapshot.funnelHistory || funnelHistory,
    replaceState: {
      data: snapshot.data,
      funnelSteps: snapshot.funnelSteps || null,
      funnelHistory: snapshot.funnelHistory || null,
    },
  }
}

const getStripeSetupIntentBackupKey = (caseId) => `cv_stripe_setup_intent_${caseId}`
const getStripePaymentMethodBackupKey = (caseId) => `cv_stripe_payment_method_${caseId}`

const getScopedBackupKey = (storageKey, rawKey) =>
  storageKey ? `${storageKey}_${rawKey}` : rawKey

const writeBackupValue = (rawKey, value, storageKey) => {
  if (typeof window === 'undefined' || !value) return
  const scopedKey = getScopedBackupKey(storageKey, rawKey)
  window.localStorage.setItem(scopedKey, value)
  // Keep legacy unscoped key for backward compatibility with older reads.
  window.localStorage.setItem(rawKey, value)
}

const persistStripeContextBackup = (data = {}, storageKey = '') => {
  const caseId = String(data.case_id || '').trim()
  if (!caseId || typeof window === 'undefined') return

  const setupIntentId = String(data.stripe_setup_intent_id || '').trim()
  const paymentMethodId = String(data.stripe_payment_method_id || '').trim()

  if (setupIntentId) {
    writeBackupValue(getStripeSetupIntentBackupKey(caseId), setupIntentId, storageKey)
  }
  if (paymentMethodId) {
    writeBackupValue(getStripePaymentMethodBackupKey(caseId), paymentMethodId, storageKey)
  }
}

/**
 * Funnel Data Loader Utility
 * Handles fetching funnel data from the backend using case_id
 * Used for cross-device checkout and abandoned cart recovery
 */

// One network call per case for this page load. The checkout guard and
// FunnelContext both ask for the same case when an abandoned-cart URL opens.
const funnelDataRequestByCaseId = new Map()

const requestFunnelData = async (caseId) => {
  try {
    logger.debug('🔄 Fetching funnel data for case_id:', caseId)

    // Build the request body with case_id
    // Note: Backend expects 'entry_id' parameter for backward compatibility
    const requestBody = new FormData()
    requestBody.append('case_id', caseId)
    requestBody.append('format', 'localstorage')

    const response = await fetch('/funnel-get-data', {
      method: 'POST',
      credentials: 'include',
      body: requestBody,
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch funnel data: ${response.status} ${response.statusText}`)
    }

    const result = await response.json()

    logger.debug('🔍 Funnel data response:', result)

    if (result.success && result.data) {
      logger.debug('✅ Funnel data fetched successfully')
      applySourceFromFunnelGetData(result)
      return {
        success: true,
        data: result.data,
      }
    } else {
      logger.warn('⚠️ Funnel data fetch unsuccessful:', result.message || result.data?.message)
      return {
        success: false,
        message: result.message || result.data?.message || 'Failed to fetch funnel data',
      }
    }
  } catch (error) {
    logger.error('❌ Error fetching funnel data:', error)
    return {
      success: false,
      message: error.message,
    }
  }
}

/**
 * Fetch funnel data from backend by case_id.
 * Repeat calls for the same case share the in-flight request, and a
 * successful response is reused for the rest of this page load.
 * @param {string} caseId - The case_id to fetch data for (UUID from URL)
 * @param {{ forceRefresh?: boolean }} [options] - Pass forceRefresh to bypass
 *   the page-load cache (e.g. tab visibility revalidate after payment elsewhere).
 * @returns {Promise<Object>} Promise resolving to funnel data response
 */
export const fetchFunnelData = (caseId, options = {}) => {
  const key = String(caseId || '').trim()
  if (!key) {
    logger.warn('⚠️ No case_id provided for fetching funnel data')
    return Promise.resolve({
      success: false,
      message: 'case_id is required',
    })
  }

  if (options.forceRefresh) {
    funnelDataRequestByCaseId.delete(key)
  }

  const existing = funnelDataRequestByCaseId.get(key)
  if (existing) {
    logger.debug('⏭️ Reusing funnel-get-data request for case_id:', key)
    return existing
  }

  const request = requestFunnelData(key).then((result) => {
    if (!result?.success) {
      funnelDataRequestByCaseId.delete(key)
    }
    return result
  })
  funnelDataRequestByCaseId.set(key, request)
  return request
}

/**
 * Load funnel data into localStorage and context using case_id
 * Used for cross-device checkout and abandoned cart recovery
 * @param {string} caseId - The case_id to fetch data for (UUID from URL path)
 * @param {string} storageKey - The localStorage key to store data in
 * @param {Object} contextUpdaters - Object with update functions: { updateFunnelData, setFunnelSteps, setFunnelHistory }
 * @returns {Promise<Object>} Promise resolving to load result
 */
export const loadFunnelDataFromBackend = async (caseId, storageKey, contextUpdaters) => {
  try {
    logger.debug('╔═══════════════════════════════════════════════════════════╗')
    logger.debug('║      LOADING FUNNEL DATA FROM BACKEND                    ║')
    logger.debug('╚═══════════════════════════════════════════════════════════╝')
    logger.debug('📥 Case ID:', caseId)
    logger.debug('📦 Storage Key:', storageKey)

    // Fetch data from backend using case_id
    const result = await fetchFunnelData(caseId)

    if (result.success && result.data) {
      logger.debug('✅ Funnel data fetched successfully')
      
      // Backend returns the complete localStorage structure:
      // { data: {...}, funnelSteps: {...}, funnelHistory: {...}, timestamp: "..." }
      const completeData = result.data
      
      logger.debug('📊 Structure received:')
      logger.debug('  - data fields:', completeData.data ? Object.keys(completeData.data).length : 0)
      logger.debug('  - funnelSteps:', completeData.funnelSteps)
      logger.debug('  - funnelHistory keys:', completeData.funnelHistory ? Object.keys(completeData.funnelHistory).length : 0)
      logger.debug('  - timestamp:', completeData.timestamp)

      // Replace the saved blob with the API response. Spreading the existing
      // localStorage object keeps the previous tab's fields.
      const hasMap = (value) => value && typeof value === 'object' && !Array.isArray(value)
      const apiData = { ...(completeData.data || {}) }
      if (caseId) {
        apiData.case_id = String(caseId).trim()
      }

      // An ?affid= on this URL is the current attribution; the case's stored
      // affid is from the original visit.
      const urlAffid = new URLSearchParams(window.location.search).get('affid')
      if (urlAffid && apiData.affid !== urlAffid) {
        logger.debug('🔗 URL affid overrides funnel-get-data affid:', { url: urlAffid, api: apiData.affid })
        apiData.affid = urlAffid
      }
      const apiFunnelSteps = hasMap(completeData.funnelSteps) ? completeData.funnelSteps : null
      const apiFunnelHistory = hasMap(completeData.funnelHistory) ? completeData.funnelHistory : null

      const mergedData = {
        ...completeData,
        data: apiData,
        ...(apiFunnelSteps ? { funnelSteps: apiFunnelSteps } : {}),
        ...(apiFunnelHistory ? { funnelHistory: apiFunnelHistory } : {}),
      }

      persistStripeContextBackup(mergedData.data, storageKey)

      // A direct Everflow click in this visit replaces the transaction id
      // stored on the case. Until that click resolves, the API value stands.
      const directHitTransactionId = getDirectHitTransactionId()
      if (directHitTransactionId) {
        mergedData.data.ef_tid = directHitTransactionId
        logger.debug('🔑 Direct-hit session — ef_tid set from the Everflow click:', directHitTransactionId)
      }

      rememberLoadSnapshot(storageKey, {
        data: mergedData.data,
        funnelSteps: apiFunnelSteps,
        funnelHistory: apiFunnelHistory,
      })
      localStorage.setItem(storageKey, JSON.stringify(mergedData))
      logger.debug('💾 Replaced localStorage with funnel-get-data:', storageKey)

      // Replace memory. Merging onto the previous session keeps fields the
      // API did not send, and the save effect writes those fields back.
      if (contextUpdaters) {
        if (contextUpdaters.updateFunnelData && mergedData.data) {
          contextUpdaters.updateFunnelData(() => mergedData.data)
          logger.debug('🔄 Replaced FunnelContext.funnelData with funnel-get-data')
        }

        if (contextUpdaters.setFunnelSteps && apiFunnelSteps) {
          contextUpdaters.setFunnelSteps(() => apiFunnelSteps)
          logger.debug('🔄 Replaced FunnelContext.funnelSteps with funnel-get-data')
        }

        if (contextUpdaters.setFunnelHistory && apiFunnelHistory) {
          contextUpdaters.setFunnelHistory(() => apiFunnelHistory)
          logger.debug('🔄 Replaced FunnelContext.funnelHistory with funnel-get-data')
        }
      }

      return {
        success: true,
        data: mergedData,
      }
    } else {
      logger.warn('⚠️ Failed to fetch funnel data:', result.message)
      return {
        success: false,
        message: result.message,
      }
    }
  } catch (error) {
    logger.error('❌ Error loading funnel data from backend:', error)
    return {
      success: false,
      message: error.message,
    }
  }
}
